import { useEffect, useRef } from 'react';
import aa from 'search-insights';

const ALGOLIA_QUERY_ID_KEY = 'algolia_queryID';
const TRACKED_EVENTS_KEY = 'trackedEvents';

type AlgoliaInsightsTrackerProps = {
  config: {
    apiKey: string;
    appId: string;
    indexName: string;
  };
  objectID: string;
  userToken: string;
};

const AlgoliaInsightsTracker = ({
  config: { apiKey, appId, indexName },
  objectID,
  userToken,
}: AlgoliaInsightsTrackerProps) => {
  const initialized = useRef(false);

  const getTrackedEvents = (): Record<string, boolean> => {
    try {
      return JSON.parse(sessionStorage.getItem(TRACKED_EVENTS_KEY) || '{}');
    } catch {
      return {};
    }
  };

  const setTrackedEvents = (events: Record<string, boolean>): void =>
    sessionStorage.setItem(TRACKED_EVENTS_KEY, JSON.stringify(events));

  const sendConversion = (
    eventName: string,
    indexName: string,
    objectID: string,
    algoliaQueryID: string,
    userToken: string
  ) => {
    if (!eventName || !indexName || !objectID || !algoliaQueryID || !userToken)
      return;
    aa('convertedObjectIDsAfterSearch', {
      eventName,
      index: indexName,
      userToken,
      objectIDs: [objectID],
      queryID: algoliaQueryID,
    });
  };

  const trackConversionOncePerSession = (
    eventName: string,
    indexName: string,
    objectID: string,
    algoliaQueryID: string,
    userToken: string
  ) => {
    const trackedEvents = getTrackedEvents();
    if (!trackedEvents[eventName]) {
      sendConversion(eventName, indexName, objectID, algoliaQueryID, userToken);
      trackedEvents[eventName] = true;
      setTrackedEvents(trackedEvents);
    }
  };

  const shouldClearSession = (
    algoliaQueryID: string | null,
    referrer: string
  ) => {
    if (!algoliaQueryID || !referrer) return true;
    if (referrer.includes('/vehicles') || referrer.includes('/quotes/start'))
      return false;

    return true;
  };

  useEffect(() => {
    if (!objectID) return;

    const algoliaQueryID = sessionStorage.getItem(ALGOLIA_QUERY_ID_KEY);
    const referrer = document.referrer;

    if (shouldClearSession(algoliaQueryID, referrer)) {
      sessionStorage.removeItem(ALGOLIA_QUERY_ID_KEY);
      sessionStorage.removeItem(TRACKED_EVENTS_KEY);
      return;
    }

    if (initialized.current) return;
    initialized.current = true;

    aa('init', { appId, apiKey, useCookie: false });
    aa('setUserToken', userToken);

    const handleClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement)?.closest<HTMLElement>(
        '[data-insight-event]'
      );
      if (!el || !el.dataset.insightEvent || !el.dataset.eventName) return;

      const { eventName } = el.dataset;

      if (objectID && indexName && algoliaQueryID && eventName) {
        trackConversionOncePerSession(
          eventName,
          indexName,
          objectID,
          algoliaQueryID,
          userToken
        );
      }
    };

    document.body.addEventListener('click', handleClick);

    return () => {
      document.body.removeEventListener('click', handleClick);
    };
  }, []);

  return null;
};

export default AlgoliaInsightsTracker;
